/**
 * Hands-free wake word (RW-4.43): the seam an engine plugs into.
 *
 * An engine listens to a microphone stream on the device and calls `onWake`
 * when it hears its phrase. Nothing is sent anywhere while it listens; only
 * the call it starts uses the network.
 *
 * One engine today: openWakeWord "Hey Jarvis", whose model is licensed
 * non-commercial (`public/wakeword/LICENSE.md`). It is to be replaced before
 * Omni charges anyone — RW-4.43 [LEGAL]. Another engine (Picovoice "Hey
 * Omni", a custom model) is one factory in `WAKE_ENGINES`.
 */
import workletUrl from "./capture.worklet.ts?worker&url";

export interface WakeEngine {
  /** Resolves once the engine is listening; rejects if it cannot start. */
  start(stream: MediaStream): Promise<void>;
  stop(): void;
}

export interface WakeEngineEntry {
  /** What the user says, as shown in the UI. */
  phrase: string;
  create: (onWake: () => void) => WakeEngine;
}

/** openWakeWord's pipeline with one phrase head from `public/wakeword/`. */
function openWakeWord(model: string, onWake: () => void): WakeEngine {
  let ctx: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioWorkletNode | null = null;
  let worker: Worker | null = null;

  const stop = () => {
    source?.disconnect();
    node?.disconnect();
    void ctx?.close();
    worker?.terminate();
    ctx = source = node = worker = null;
  };

  return {
    async start(stream) {
      const w = new Worker(new URL("./oww.worker.ts", import.meta.url), { type: "module" });
      worker = w;
      const ready = new Promise<void>((resolve, reject) => {
        w.onmessage = (ev: MessageEvent<{ type: string; message?: string }>) => {
          if (ev.data.type === "ready") resolve();
          else if (ev.data.type === "wake") onWake();
          else if (ev.data.type === "error") reject(new Error(ev.data.message));
        };
        w.onerror = (ev) => reject(new Error(ev.message || "wake word worker failed"));
      });
      try {
        ctx = new AudioContext();
        await ctx.audioWorklet.addModule(workletUrl);
        // No outputs: nothing is played back, and the node still runs while
        // its process() returns true.
        node = new AudioWorkletNode(ctx, "omni-wake-capture", { numberOfOutputs: 0 });
        const { port1, port2 } = new MessageChannel();
        w.postMessage({ type: "audio-port", port: port1, model }, [port1]);
        node.port.postMessage({ port: port2 }, [port2]);
        source = ctx.createMediaStreamSource(stream);
        source.connect(node);
        await ready;
      } catch (err) {
        stop();
        throw err;
      }
    },
    stop,
  };
}

export const WAKE_ENGINES: Record<string, WakeEngineEntry> = {
  openwakeword: { phrase: "Hey Jarvis", create: (onWake) => openWakeWord("hey_jarvis_v0.1.onnx", onWake) },
};
export const DEFAULT_WAKE_ENGINE = "openwakeword";
